import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { isNativeAndroid } from '../../utils/platform.utils';

interface Option {
  value: string | number;
  label: string;
}

interface CustomSelectProps {
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
}

/**
 * Custom select component that uses:
 * - Native <select> on iOS and PWA (works well)
 * - Custom modal dropdown on Android (native picker is broken)
 */
export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  required = false,
  style = {}
}) => {
  // Use native select on iOS and PWA, custom modal on Android only
  const useCustomModal = isNativeAndroid();

  // For native select (iOS/PWA)
  if (!useCustomModal) {
    return (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '18px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          color: value ? '#111827' : '#9ca3af',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...style
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  // Custom modal for Android
  return <AndroidCustomSelect
    value={value}
    onChange={onChange}
    options={options}
    placeholder={placeholder}
    disabled={disabled}
    style={style}
  />;
};

/**
 * Android-specific custom modal select
 * Renders a modal dropdown instead of native picker (which is broken on Android WebView)
 */
const AndroidCustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  style = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Find selected option label
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside as any);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (optionValue: string | number) => {
    onChange(String(optionValue));
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      {/* Select trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '18px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          color: selectedOption ? '#111827' : '#9ca3af',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          outline: 'none'
        }}
      >
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {displayText}
        </span>
        <ChevronDown
          size={24}
          color="#6b7280"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            flexShrink: 0
          }}
        />
      </button>

      {/* Dropdown overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 9998
            }}
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown menu - centered modal style for mobile */}
          <div
            style={{
              position: 'fixed',
              left: '16px',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              zIndex: 9999,
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '700',
                color: '#374151'
              }}>
                {placeholder}
              </h3>
            </div>

            {/* Options list */}
            <div style={{
              overflowY: 'auto',
              flex: 1
            }}>
              {options.map((option, index) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      fontSize: '18px',
                      backgroundColor: isSelected ? '#fef2f2' : 'white',
                      color: '#111827',
                      border: 'none',
                      borderBottom: index < options.length - 1 ? '1px solid #f3f4f6' : 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ flex: 1 }}>{option.label}</span>
                    {isSelected && (
                      <Check size={24} color="#dc2626" strokeWidth={3} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Cancel button */}
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb'
            }}>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#6b7280',
                  backgroundColor: 'white',
                  border: '2px solid #d1d5db',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomSelect;
